import {
  type NonZeroVector,
  type Point,
  point,
  polygon,
  vector,
} from '../../elements';
import {
  getCircleClippingPoint,
  getCircleHitArea,
} from '../../elements/circle';
import {
  getRectangleClippingPoint,
  getRectangleHitArea,
} from '../../elements/rectangle';
import { ARC_SMALL_LEFT, SvgPathBuilder } from '../../svg/SvgPathBuilder';
import { wrapInSvgTag } from '../../svg/wrapInSvgTag';
import { assertType } from '../../types/assertType';
import { roundTo2, squared } from '../../utils/numbers';
import type { NodeStyle } from '../NodeStyle';
import type { NodeIconOptions, NodeTypeIcon } from './NodeTypeIcon';
import { OUTLINE_TOTAL_SIZE } from './drawIconBackground';
import { createDefaultLabelStyle } from './style';

export interface Scale {
  (x: number): number;
  (x: Point): Point;
}

// overloaded Scale implementation can't be typed without `any`

export const createCircleNodeTypeIcon = (
  size: number,
  createIconSvg: (options?: NodeIconOptions) => string,
): NodeTypeIcon => {
  const withNodeStyle = makeWithNodeStyle1(size);
  return {
    createIconSvg,
    makeGetClippingPoint: withNodeStyle(
      (s) => (direction: NonZeroVector) =>
        getCircleClippingPoint(s / 2, direction),
    ),
    getHitArea: withNodeStyle((s) => getCircleHitArea(s / 2)),
    getLabelStyle: withNodeStyle(createDefaultLabelStyle),
    getMaxRadius: withNodeStyle((s) => s / 2),
    getSize: withNodeStyle((s) => ({ width: s, height: s })),
    shape: 'circle',
  };
};

export const createRectangleNodeTypeIcon = (
  width: number,
  height: number,
  createIconSvg: (options?: NodeIconOptions) => string,
): NodeTypeIcon => {
  const withNodeStyle = makeWithNodeStyle(width, height);
  return {
    createIconSvg,
    makeGetClippingPoint: withNodeStyle(
      (w, h) => (direction: NonZeroVector) =>
        getRectangleClippingPoint(w, h, direction),
    ),
    getHitArea: withNodeStyle(getRectangleHitArea),
    getLabelStyle: withNodeStyle((_w, h) => createDefaultLabelStyle(h)),
    getMaxRadius: withNodeStyle((w, h) =>
      Math.sqrt(squared(w / 2) + squared(h / 2)),
    ),
    getSize: withNodeStyle((w, h) => ({ width: w, height: h })),
    shape: 'rectangle',
  };
};

export const createSquareNodeTypeIcon = (
  size: number,
  createIconSvg: (options?: NodeIconOptions) => string,
): NodeTypeIcon => createRectangleNodeTypeIcon(size, size, createIconSvg);

export const getArrowSvgPath = ({
  from,
  to,
  headWidth,
  headHeight,
  tailWidth,
  headSkew = 0,
}: {
  from: Point;
  to: Point;
  headHeight: number;
  headSkew?: number;
  headWidth: number;
  tailWidth: number;
}): string => {
  const direction = vector.fromAtoB(from, to);
  assertType(vector.isNonZero, direction);
  const vector0 = vector.normalize(direction);
  const vector90 = vector.perp(vector0);
  const vector180 = vector.reverse(vector0);
  const vector270 = vector.reverse(vector90);
  const headRootBase = point.createOnVector(
    to,
    vector180,
    headHeight - headSkew,
  );
  const headSideBase = point.createOnVector(to, vector180, headHeight);

  const halfHeadWidth = headWidth / 2;
  const halfTailWidth = tailWidth / 2;

  return polygon.getD([
    to,
    point.createOnVector(headSideBase, vector90, halfHeadWidth),
    point.createOnVector(headRootBase, vector90, halfTailWidth),
    point.createOnVector(from, vector90, halfTailWidth),
    point.createOnVector(from, vector270, halfTailWidth),
    point.createOnVector(headRootBase, vector270, halfTailWidth),
    point.createOnVector(headSideBase, vector270, halfHeadWidth),
  ]);
};

export const getRoundedRectanglePath = (
  x: number,
  y: number,
  width: number,
  height: number,
  borderRadius: number,
): string =>
  new SvgPathBuilder()
    .moveTo(x, y + borderRadius)
    .circleTo(borderRadius, ARC_SMALL_LEFT)(x + borderRadius, y)
    .hLineTo(x + width - borderRadius)
    .circleTo(borderRadius, ARC_SMALL_LEFT)(x + width, y + borderRadius)
    .vLineTo(y + height - borderRadius)
    .circleTo(borderRadius, ARC_SMALL_LEFT)(
      x + width - borderRadius,
      y + height,
    )
    .hLineTo(x + borderRadius)
    .circleTo(borderRadius, ARC_SMALL_LEFT)(x, y + height - borderRadius)
    .buildClosed();

/**
 * Takes a function of shape (width, height) => T and transforms it to (NodeStyle?) => T
 */
const makeWithNodeStyle =
  (width: number, height: number) =>
  <T>(fn: (width: number, height: number) => T) =>
  (nodeStyle: NodeStyle | undefined): T => {
    const outlineDouble = nodeStyle?.outline ? OUTLINE_TOTAL_SIZE * 2 : 0;
    return fn(width + outlineDouble, height + outlineDouble);
  };

/**
 * Variant of makeWithNodeStyle for nodes with both dimensions equal
 */
const makeWithNodeStyle1 = (size: number) => makeWithNodeStyle(size, size);

export const wrapNodeIconInSvgTag = (
  iconSvg: string,
  width: number,
  height: number,
  zeroPosition: 'center' | 'topLeft',
  options: NodeIconOptions,
): string => {
  const { left, top } =
    zeroPosition === 'center'
      ? {
          left: -width / 2,
          top: -height / 2,
        }
      : {
          left: 0,
          top: 0,
        };
  const scale = options.scale ?? 1;
  const outlineSize = options.style?.outline ? OUTLINE_TOTAL_SIZE : 0;
  const widthWithOutline = width + 2 * outlineSize;
  const heightWithOutline = height + 2 * outlineSize;

  return wrapInSvgTag(iconSvg, {
    width: Math.round(widthWithOutline * scale),
    height: Math.round(heightWithOutline * scale),
    viewBox: `${roundTo2(left - outlineSize)} ${roundTo2(
      top - outlineSize,
    )} ${roundTo2(widthWithOutline)} ${roundTo2(heightWithOutline)}`,
    ...options.attributes,
  });
};
